import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { Alert, Empty, Muted, PageHeader, Section, Tabs } from '@/components/ui'
import { fetchAtsDashboard, fetchVagas } from './api'
import { KpiStrip } from './components/KpiStrip'
import { NovaVagaForm } from './components/NovaVagaForm'
import { VagaCard } from './components/VagaCard'
import type { RecrutTab } from './types'

const TABS = [
  { id: 'resumo', label: 'Resumo' },
  { id: 'vagas', label: 'Vagas' },
  { id: 'pipeline', label: 'Pipeline' },
] as const

export function RecrutamentoPage() {
  const { canEdit } = useAuth()
  const [tab, setTab] = useState<RecrutTab>('resumo')
  const [status, setStatus] = useState('aberta')
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [showNova, setShowNova] = useState(false)

  const dash = useQuery({
    queryKey: ['ats', 'dashboard'],
    queryFn: fetchAtsDashboard,
  })

  const vagas = useQuery({
    queryKey: ['ats', 'vagas', status, q],
    queryFn: () => fetchVagas({ status: status || undefined, search: q || undefined }),
  })

  const lista = vagas.data?.dados || []
  const total = vagas.data?.meta?.total ?? lista.length

  const funil = useMemo(() => {
    const f = dash.data?.funil_pipeline || {}
    return Object.entries(f).sort((a, b) => b[1] - a[1])
  }, [dash.data])

  return (
    <section className="hi-page">
      <PageHeader
        title="Recrutamento"
        subtitle="Vagas, pipeline e criação básica na API. IA e publicações avançadas continuam no legado."
        actions={
          <>
            {canEdit('recrutamento') ? (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setShowNova((v) => !v)
                  setTab('vagas')
                }}
              >
                {showNova ? 'Fechar formulário' : '+ Nova vaga'}
              </button>
            ) : null}
            <Link className="btn btn-secondary" to="/app/plano-contratacao">
              Plano de contratação
            </Link>
            <a className="btn btn-secondary" href={legacyAppUrl('#/recrutamento')}>
              Legado
            </a>
          </>
        }
      />

      {showNova ? <NovaVagaForm onClose={() => setShowNova(false)} /> : null}

      <Tabs items={TABS} value={tab} onChange={setTab} label="Abas do recrutamento" />

      {tab === 'resumo' ? (
        <div className="hi-stack">
          {dash.isLoading ? <Muted>Carregando indicadores…</Muted> : null}
          {dash.isError ? (
            <Alert tone="danger">
              {dash.error instanceof ApiError ? dash.error.message : 'Falha ao carregar dashboard.'}
            </Alert>
          ) : null}
          {dash.data ? <KpiStrip data={dash.data} /> : null}
          {funil.length > 0 ? (
            <Section title="Funil atual">
              <ul className="hi-funil">
                {funil.map(([etapa, n]) => (
                  <li key={etapa}>
                    <span>{etapa}</span>
                    <strong>{n}</strong>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </div>
      ) : null}

      {tab === 'vagas' ? (
        <div className="hi-stack">
          <form
            className="hi-filters"
            onSubmit={(e) => {
              e.preventDefault()
              setQ(search.trim())
            }}
          >
            <input
              type="search"
              placeholder="Buscar título, cargo, departamento…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Todos os status</option>
              <option value="aberta">Abertas</option>
              <option value="pausada">Pausadas</option>
              <option value="fechada">Fechadas</option>
              <option value="rascunho">Rascunho</option>
            </select>
            <button type="submit" className="btn btn-primary">
              Filtrar
            </button>
          </form>
          <Muted>{vagas.isFetching ? 'Atualizando…' : `${total} vaga(s)`}</Muted>
          {vagas.isError ? (
            <Alert tone="danger">
              {vagas.error instanceof ApiError ? vagas.error.message : 'Falha ao listar vagas.'}
            </Alert>
          ) : null}
          <div className="hi-vaga-grid">
            {lista.map((v) => (
              <VagaCard key={v.id} vaga={v} />
            ))}
          </div>
          {!vagas.isLoading && lista.length === 0 ? <Empty>Nenhuma vaga neste filtro.</Empty> : null}
        </div>
      ) : null}

      {tab === 'pipeline' ? (
        <div className="hi-stack">
          <Muted>Escolha uma vaga para abrir o Kanban.</Muted>
          <ul className="hi-pick-list">
            {(vagas.data?.dados || [])
              .filter((v) => v.status === 'aberta' || !status)
              .map((v) => (
                <li key={v.id}>
                  <Link className="card card-interactive" to={`/app/recrutamento/${v.id}?tab=pipeline`}>
                    <strong>{v.titulo}</strong>
                    <span>
                      {v.total_candidatos ?? 0} candidatos · {v.empresa_grupo_nome || '-'}
                    </span>
                  </Link>
                </li>
              ))}
          </ul>
          {vagas.isLoading ? <Muted>Carregando vagas…</Muted> : null}
        </div>
      ) : null}
    </section>
  )
}

export default RecrutamentoPage
